import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { DetailSelect, DetailTypography } from "../../styled/detailsComponents";
import React, { FC, useContext, useState } from "react";
import {
	FormControlLabel,
	IconButton,
	InputAdornment,
	MenuItem,
	Radio,
	RadioGroup,
	SelectChangeEvent,
	Stack,
	TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import {
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";

export const DetailItemStack = ({ children }) => {
	return (
		<Stack spacing={1} width="100%" flex={1} paddingX={2} alignItems="center">
			{children}
		</Stack>
	);
};

interface DetailTextProps {
	name: string;
	defaultMessage: string;
}

export const DetailText: FC<DetailTextProps> = ({ name, defaultMessage }) => {
	const { formatMessage } = useIntl();

	return (
		<DetailTypography>
			{formatMessage({
				id: `details.${name}`,
				defaultMessage,
			})}
		</DetailTypography>
	);
};

interface DetailComponentsProps {
	name: string;
	defaultValue: string | number;
	onChange: Function;
	options?: { value: string; name: string }[];
}

const languages = {
	"es-ES": ["es-ES", "Español", "Spanish", "Espanhol"],
	"en-US": ["en-US", "Inglés", "English", "Inglês"],
};

export const LanguageSelect: FC<DetailComponentsProps> = ({
	name,
	defaultValue,
	onChange,
}) => {
	const selectedValue = languages["es-ES"].includes(defaultValue.toString())
		? "es-ES"
		: languages["en-US"].includes(defaultValue.toString())
		? "en-US"
		: "es-ES";
		
	const { formatMessage } = useIntl();
	const { reservationMade } = useContext(
		MultistepContext
	) as MultistepContextValues;
	//If the locale is not in the available languages, then the default value will be spanish
	const [value, setValue] = useState(selectedValue);

	const handleChange = (event: SelectChangeEvent<unknown>) => {
		const newValue = event.target.value as string;
		setValue(newValue);
		onChange(newValue);
	};

	return (
		<DetailSelect
			name={name}
			disabled={reservationMade}
			value={value}
			onChange={handleChange}
			sx={{ maxWidth: "250px" }}
		>
			<MenuItem value={"es-ES"}>
				{formatMessage({
					id: "details.language.spanish",
					defaultMessage: "Español",
				})}
			</MenuItem>
			<MenuItem value={"en-US"}>
				{formatMessage({
					id: "details.language.english",
					defaultMessage: "Inglés",
				})}
			</MenuItem>
		</DetailSelect>
	);
};

export const NumberInput: FC<DetailComponentsProps> = ({
	name,
	defaultValue,
	onChange,
}) => {
	const { reservation, reservationMade } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const [value, setValue] = useState(defaultValue);

	const handleDecrease = () => {
		//If the input is empty and the user clicks the remove icon, then the new value will be 0
		let newValue;
		if (value === "") {
			newValue = 0;
		} else {
			// If the new value is equal or smaller than 0, the value will change to 0
			newValue = Math.max((value as number) - 1, 0);
		}
		setValue(newValue);
		onChange(newValue);
	};

	const handleIncrease = () => {
		let newValue;
		//If the input is empty and the user clicks the add icon, then the new value will be 1
		if (value === "") {
			newValue = 1;
		} else {
			newValue = (value as number) + 1;
		}
		// If the new value is equal or smaller than the number diners, the value will change
		if (newValue <= reservation.numberDiners) {
			setValue(newValue);
			onChange(newValue);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		if (inputValue === "") {
			// If the input is empty, it sets the value to 0
			setValue("");
			onChange(0);
		} else {
			const newValue = parseInt(inputValue);
			// If the new value is not a number, then we don't change the value
			if (!isNaN(newValue) && newValue <= reservation.numberDiners) {
				setValue(newValue);
				onChange(newValue);
			}
		}
	};

	return (
		<TextField
			type="text"
			value={value}
			name={name}
			disabled={reservationMade}
			variant="outlined"
			onChange={handleChange}
			sx={{ height: "50px", maxWidth: "250px", "& input": { padding: 0 } }}
			InputProps={{
				inputProps: {
					style: { textAlign: "center" },
				},
				startAdornment: (
					<InputAdornment position="start">
						<IconButton
							onClick={handleDecrease}
							size="small"
							disabled={reservationMade}
						>
							<RemoveIcon fontSize="small" />
						</IconButton>{" "}
					</InputAdornment>
				),
				endAdornment: (
					<InputAdornment position="end">
						<IconButton
							onClick={handleIncrease}
							size="small"
							disabled={reservationMade}
						>
							<AddIcon fontSize="small" />
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
};

export const RadioButtonGroup: FC<DetailComponentsProps> = ({
	name,
	defaultValue,
	onChange,
	options,
}) => {
	const { formatMessage } = useIntl();
	const { reservationMade } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const [value, setValue] = useState<string | null>(defaultValue as string);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let newValue: string | boolean = (event.target as HTMLInputElement).value;
		setValue(newValue);
		if (newValue === "true" || newValue === "false")
			newValue = newValue === "true" ? true : false;
		onChange(newValue);
	};

	return (
		<RadioGroup
			row
			name={name}
			value={value}
			onChange={handleChange}
			sx={{ justifyContent: "center" }}
		>
			{options!.map((option, index) => (
				<FormControlLabel
					key={index}
					value={option.value}
					disabled={reservationMade}
					control={<Radio />}
					label={formatMessage({
						id: `details.${name}.${option.name}`,
						defaultMessage: option.name,
					})}
					onClick={() => {
						if (option.value === value) {
							setValue(null);
							onChange(null);
						}
					}}
					sx={{ "& .MuiRadio-root": { padding: "2px 9px" } }}
				/>
			))}
		</RadioGroup>
	);
};
